import React, { useState } from 'react';
import { linkEventToPatient, linkEventToNewPatient, markEventAsGenericEvent, rejectEvent, getPendingSeries } from '../api';
import ModalNuevoPaciente from './ModalNuevoPaciente';

// Item de la bandeja de Google Calendar. Puede representar un evento suelto
// (evento.is_series === false, mismo shape de siempre) o el resumen
// colapsado de una serie recurrente (evento.is_series === true — ver
// linking.group_pending_events en el backend). Recibe el evento por props en
// vez de que ModalBandejaGoogle hardcodee su estructura.
function formatearFecha(fechaISO, isAllDay) {
  const d = new Date(fechaISO);
  if (isAllDay) {
    // Se guarda como medianoche UTC de la fecha literal que mandó Google
    // (ver sync_google_calendar._parse_google_datetime en el backend, sin
    // conversión de huso) — timeZone:'UTC' acá lee esos mismos componentes
    // de fecha tal cual, si no un profesional en Argentina (UTC-3) vería el
    // día anterior.
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
  }
  const fecha = d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const hora = d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  return `${fecha} ${hora}`;
}

// Cuántas instancias se resolvieron realmente: la fuente de verdad es
// series_result.resolved (el evento principal + los hermanos que el backend
// efectivamente resolvió), no evento.count — una instancia pasada
// (skipped_past) o en conflicto puede haber quedado afuera.
function totalResueltos(seriesResult) {
  return 1 + (seriesResult?.resolved || 0);
}

function mensajeCantidad(n, singular, plural) {
  return n === 1 ? `Evento ${singular}.` : `${n} eventos ${plural}.`;
}

function ItemEventoPendiente({ evento, pacientes, onResuelto, onPacientesActualizados }) {
  const [accion, setAccion] = useState(null); // null | 'existente'
  const [pacienteSeleccionado, setPacienteSeleccionado] = useState('');
  const [mostrarNuevoPaciente, setMostrarNuevoPaciente] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [mostrarFechas, setMostrarFechas] = useState(false);
  const [fechasSerie, setFechasSerie] = useState(null);
  const [cargandoFechas, setCargandoFechas] = useState(false);

  const pacientesActivos = (pacientes || []).filter(p => p.is_active !== false);
  // Un ítem agrupado siempre implica toda la serie — no hace falta el
  // checkbox "aplicar a toda la serie" de la 3b, evento.is_series ya lo dice.
  const aplicarSerie = !!evento.is_series;

  const cerrarAccion = () => {
    setAccion(null);
    setPacienteSeleccionado('');
    setError('');
  };

  const toggleVerFechas = async () => {
    if (mostrarFechas) {
      setMostrarFechas(false);
      return;
    }
    setMostrarFechas(true);
    if (fechasSerie) return; // ya se pidieron una vez, no repetir
    setCargandoFechas(true);
    try {
      const data = await getPendingSeries(evento.recurring_event_id);
      setFechasSerie(data);
    } catch {
      setFechasSerie([]);
    } finally {
      setCargandoFechas(false);
    }
  };

  const confirmarVincularExistente = async () => {
    if (!pacienteSeleccionado) return;
    setGuardando(true);
    setError('');
    try {
      const data = await linkEventToPatient(evento.id, Number(pacienteSeleccionado), aplicarSerie);
      const paciente = pacientesActivos.find(p => String(p.id) === pacienteSeleccionado);
      const nombre = paciente?.nombre || 'paciente';
      const n = totalResueltos(data.series_result);
      const mensaje = n === 1 ? `Evento vinculado a ${nombre}.` : `${n} eventos vinculados a ${nombre}.`;
      onResuelto(evento, mensaje, data);
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  const confirmarPacienteNuevo = async (nombre, apellido) => {
    setGuardando(true);
    setError('');
    try {
      const data = await linkEventToNewPatient(evento.id, nombre, apellido, aplicarSerie);
      setMostrarNuevoPaciente(false);
      if (onPacientesActualizados) onPacientesActualizados();
      // Mismo default que el backend si vino en blanco (linking.py).
      const nombreCompleto = `${(nombre || '').trim() || 'Nuevo'} ${(apellido || '').trim() || 'Paciente'}`;
      const n = totalResueltos(data.series_result);
      const mensaje = n === 1
        ? `Evento vinculado a ${nombreCompleto}.`
        : `${n} eventos vinculados a ${nombreCompleto}.`;
      onResuelto(evento, mensaje, data);
    } catch (err) {
      // Se deja el modal de nuevo paciente abierto (no se cierra en el
      // catch) para que el profesional pueda corregir y reintentar sin
      // perder lo que ya escribió.
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  const confirmarMarcarComoEvento = async () => {
    setGuardando(true);
    setError('');
    try {
      const data = await markEventAsGenericEvent(evento.id, aplicarSerie);
      const mensaje = mensajeCantidad(totalResueltos(data.series_result), 'marcado como evento', 'marcados como evento');
      onResuelto(evento, mensaje, data);
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  const confirmarRechazar = async () => {
    setGuardando(true);
    setError('');
    try {
      const data = await rejectEvent(evento.id, aplicarSerie);
      const mensaje = mensajeCantidad(totalResueltos(data.series_result), 'rechazado', 'rechazados');
      onResuelto(evento, mensaje, data);
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  const fechaMostrada = evento.is_series ? evento.next_start : evento.start;

  return (
    <div className="bandeja-item">
      <div className="bandeja-item-info">
        <span className="bandeja-item-titulo">{evento.title || '(sin título)'}</span>
        <span className="bandeja-item-fecha">
          {evento.is_series
            ? `${evento.count} eventos · próximo ${formatearFecha(fechaMostrada, evento.is_all_day)}`
            : formatearFecha(fechaMostrada, evento.is_all_day)}
          {evento.is_all_day && <span className="bandeja-badge-alldia">Todo el día</span>}
        </span>
        {evento.is_series && (
          <button type="button" className="bandeja-btn-ver-fechas" onClick={toggleVerFechas} disabled={guardando}>
            {mostrarFechas ? 'Ocultar fechas' : 'Ver fechas'}
          </button>
        )}
        {mostrarFechas && (
          <div className="bandeja-fechas-serie">
            {cargandoFechas && <span className="bandeja-fechas-vacio">Cargando fechas...</span>}
            {!cargandoFechas && fechasSerie && fechasSerie.length === 0 && (
              <span className="bandeja-fechas-vacio">No se encontraron fechas.</span>
            )}
            {!cargandoFechas && fechasSerie && fechasSerie.length > 0 && (
              <ul className="bandeja-fechas-lista">
                {fechasSerie.map(f => (
                  <li key={f.id}>{formatearFecha(f.start, f.is_all_day)}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {error && <div className="modal-mensaje-error">{error}</div>}

      {accion === 'existente' ? (
        <div className="bandeja-form-vincular">
          <select
            className="ficha-input cal-select"
            value={pacienteSeleccionado}
            onChange={e => setPacienteSeleccionado(e.target.value)}
            disabled={guardando}
          >
            <option value="">Seleccionar paciente...</option>
            {pacientesActivos.map(p => (
              <option key={p.id} value={p.id}>{p.nombre}</option>
            ))}
          </select>
          <div className="bandeja-item-acciones">
            <button type="button" className="modal-btn-cancelar" onClick={cerrarAccion} disabled={guardando}>
              Cancelar
            </button>
            <button
              type="button"
              className="modal-btn-guardar"
              onClick={confirmarVincularExistente}
              disabled={guardando || !pacienteSeleccionado}
            >
              {guardando ? 'Vinculando...' : 'Confirmar'}
            </button>
          </div>
        </div>
      ) : (
        <div className="bandeja-item-acciones">
          <button type="button" className="bandeja-btn-accion" onClick={() => setAccion('existente')} disabled={guardando}>
            Vincular a paciente existente
          </button>
          <button type="button" className="bandeja-btn-accion" onClick={() => setMostrarNuevoPaciente(true)} disabled={guardando}>
            Paciente nuevo
          </button>
          <button type="button" className="bandeja-btn-accion" onClick={confirmarMarcarComoEvento} disabled={guardando}>
            {guardando ? 'Guardando...' : 'Marcar como evento'}
          </button>
          <button
            type="button"
            className="bandeja-btn-accion bandeja-btn-rechazar"
            onClick={confirmarRechazar}
            disabled={guardando}
            title="No se vuelve a mostrar en esta bandeja, pero el evento sigue intacto en tu Google Calendar."
          >
            {guardando ? 'Guardando...' : 'Rechazar'}
          </button>
        </div>
      )}

      {mostrarNuevoPaciente && (
        <ModalNuevoPaciente
          nombreInicial={evento.title || ''}
          guardando={guardando}
          textoAlerta="Se va a crear el paciente y vincular este evento como su turno."
          textoBoton="Crear y vincular"
          textoBotonGuardando="Vinculando..."
          errorExterno={error}
          onConfirmar={confirmarPacienteNuevo}
          onCerrar={() => { setMostrarNuevoPaciente(false); setError(''); }}
        />
      )}
    </div>
  );
}

export default ItemEventoPendiente;
